import React, { useRef, useState, useEffect } from "react";
import type { ComponentType } from "react";
import { FaBars } from "react-icons/fa";

/**
 * U2000ResponsiveToolbar — Composant générique pour afficher une liste d'actions sous forme de boutons, avec gestion responsive (menu hamburger).
 */

export interface U2000ToolbarActionItem {
  key: string;
  label: React.ReactNode;
  handleClick: () => void;
  icon?: ComponentType<any>;
  disabled?: boolean;
}

export interface U2000ToolbarAction {
  key: string;
  label: string;
  hasRole: boolean;
  /** If present and false, the action is hidden. Default is true when undefined. */
  visible?: boolean;
  disabled?: boolean;
  handleClick: () => void;
  icon?: ComponentType<any>;
  Component: ComponentType<any>;
  /** When set, this action is a dropdown: Component receives items; in hamburger mode each item is shown as a separate button. */
  items?: U2000ToolbarActionItem[];
  [key: string]: any;
}

export interface U2000ResponsiveToolbarProps {
  actions: U2000ToolbarAction[];
  className?: string;
}

function U2000ResponsiveToolbar({ actions, className = "" }: U2000ResponsiveToolbarProps) {
  const [showHamburger, setShowHamburger] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const btnsContainerRef = useRef<HTMLDivElement | null>(null);
  const btnsWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (!btnsContainerRef.current || !btnsWrapperRef.current) return;
      // Reserve some space for the hamburger button to avoid buttons wrapping
      const HAMBURGER_RESERVE = 40; // px, adjust if your hamburger button is wider
      setShowHamburger(btnsWrapperRef.current.scrollWidth + HAMBURGER_RESERVE > btnsContainerRef.current.offsetWidth);
    };
    checkOverflow();
    const resizeObserver = new window.ResizeObserver(checkOverflow);
    if (btnsContainerRef.current) resizeObserver.observe(btnsContainerRef.current);
    window.addEventListener("resize", checkOverflow);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  return (
    <div ref={btnsContainerRef} className={`w-full relative ${className}`}>
      {/* Wrapper invisible pour mesurer la largeur réelle des boutons */}
      <div ref={btnsWrapperRef} className="flex invisible absolute left-0 top-0 pointer-events-none h-0 overflow-hidden gap-2">
        {actions
          .filter((a) => a.hasRole && a.visible !== false)
          .map((action) => (
            <action.Component {...action} items={action.items ?? []} key={action.key} />
          ))}
      </div>

      {/* Affichage réel des boutons ou menu hamburger */}
      {!showHamburger ? (
        <div className="flex gap-2 flex-nowrap justify-start overflow-hidden">
          {actions
            .filter((a) => a.hasRole && a.visible !== false)
            .map((action) => (
              <action.Component {...action} items={action.items ?? []} key={action.key} />
            ))}
        </div>
      ) : (
        <div className="flex justify-start relative">
          <button
            type="button"
            aria-label="Menu"
            className="h-[46px] w-[46px] flex items-center justify-center rounded-lg border border-gray-300 bg-gray-100 
                       transition-all duration-200 hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            <FaBars className="h-5 w-5 text-gray-700" />
          </button>

          {isMenuOpen && (
            <div
              className="absolute top-[110%] left-0 bg-white border border-gray-200 rounded-lg shadow-lg 
                         z-1000 min-w-[200px] p-2 animate-slide-up"
              onMouseLeave={() => setIsMenuOpen(false)}
            >
              {actions.flatMap((action) => {
                if (!action.hasRole || action.visible === false) return [];
                if (action.items && action.items.length > 0) {
                  return action.items.map((item) => {
                    const ItemIcon = item.icon ?? action.icon;
                    return (
                      <button
                        key={`${action.key}-${item.key}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-left 
                                   text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed
                                   transition-colors duration-150"
                        onClick={() => {
                          setIsMenuOpen(false);
                          item.handleClick();
                        }}
                        disabled={item.disabled ?? action.disabled}
                      >
                        {ItemIcon && <ItemIcon className="w-4 h-4 text-gray-500 shrink-0" />}
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  });
                }
                return [
                  <button
                    key={action.key}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-left 
                               text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed
                               transition-colors duration-150"
                    onClick={() => {
                      setIsMenuOpen(false);
                      action.handleClick();
                    }}
                    disabled={action.disabled}
                  >
                    {action.icon && <action.icon className="w-4 h-4 text-gray-500 shrink-0" />}
                    <span className="truncate">{action.label}</span>
                  </button>,
                ];
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default U2000ResponsiveToolbar;
